// Subscriber phone+OTP login. Mobile-first.

import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth.js';
import { current_language, set_language } from '../i18n/index.js';
import { friendly_error } from '../lib/friendly-error.js';

export function SubLogin(): ReactElement {
  const { t } = useTranslation();
  const { send_otp_subscriber, verify_otp_subscriber } = useAuth();
  const lang = current_language();

  const [phone, set_phone] = useState('+91');
  const [otp, set_otp] = useState('');
  const [stage, set_stage] = useState<'phone' | 'otp'>('phone');
  const [busy, set_busy] = useState(false);
  const [err, set_err] = useState<string | null>(null);

  const submit_phone = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!/^\+91[6-9]\d{9}$/.test(phone)) {
      set_err(t('sub.login.bad_phone', 'Enter a valid +91 phone number'));
      return;
    }
    set_busy(true);
    set_err(null);
    try {
      await send_otp_subscriber(phone);
      set_stage('otp');
    } catch (e2) {
      set_err(friendly_error(e2));
    } finally {
      set_busy(false);
    }
  };

  const submit_otp = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      set_err('6-digit code required');
      return;
    }
    set_busy(true);
    set_err(null);
    try {
      await verify_otp_subscriber(phone, otp);
      // navigation happens via auth state change in App
    } catch (e2) {
      set_err(friendly_error(e2));
    } finally {
      set_busy(false);
    }
  };

  const input_style = {
    padding: '12px',
    border: '1px solid var(--line-strong)',
    borderRadius: 8,
    fontFamily: 'var(--font-mono)',
    fontSize: 16,
    background: 'var(--surface)',
    color: 'var(--ink)',
  } as const;
  const err_box = (
    <div style={{ fontSize: 12, color: 'var(--bad)', background: 'var(--bad-soft)', padding: '6px 10px', borderRadius: 6 }}>{err}</div>
  );

  return (
    <div className="sub-app">
      <header className="sub-top" style={{ padding: '26px 16px', display: 'block', textAlign: 'center', position: 'static' }}>
        <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 24, fontWeight: 600 }}>
          {t('sub.login.title', 'My Chits')}
        </div>
        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>{t('app_name_en')}</div>
      </header>
      <main className="sub-main" style={{ padding: '28px 24px' }}>
        {stage === 'phone' ? (
          <form onSubmit={submit_phone} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('sub.login.phone_label', 'Your phone number')}</label>
            <input type="tel" value={phone} onChange={(e) => set_phone(e.target.value)} placeholder="+919876543210" style={input_style} autoFocus inputMode="tel" />
            {err && err_box}
            <button type="submit" disabled={busy} className="btn primary" style={{ justifyContent: 'center', padding: '12px', fontSize: 15 }}>
              {busy ? '…' : t('actions.send_otp', 'Send OTP')}
            </button>
          </form>
        ) : (
          <form onSubmit={submit_otp} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('sub.login.otp_label', '6-digit OTP sent to')} {phone}</label>
            <input type="tel" value={otp} onChange={(e) => set_otp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" style={{ ...input_style, fontSize: 26, letterSpacing: '0.4em', textAlign: 'center' }} autoFocus inputMode="numeric" maxLength={6} />
            {err && err_box}
            <button type="submit" disabled={busy || otp.length !== 6} className="btn primary" style={{ justifyContent: 'center', padding: '12px', fontSize: 15 }}>
              {busy ? '…' : t('actions.verify', 'Verify')}
            </button>
            <button type="button" onClick={() => { set_stage('phone'); set_otp(''); set_err(null); }} className="btn ghost sm" style={{ alignSelf: 'center' }}>
              {t('actions.back', 'Change number')}
            </button>
          </form>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 32, textAlign: 'center' }}>
          <button type="button" onClick={() => set_language(lang === 'ta' ? 'en' : 'ta')} className="btn ghost sm" style={{ color: 'var(--ink-3)' }}>
            {t('actions.switch_lang', 'Switch language')}
          </button>
        </div>
      </main>
    </div>
  );
}
